import { useCallback, useEffect, useState } from 'react';

import { useLocation, useNavigate } from '@remix-run/react';

interface ReturnProps {
  highlightedSectionId: string | null;
  highlightedNodeId: string | null;
}

interface Props {
  isInitialLoadFromSwarm: boolean;
  publicationId: string;
}

const useWebBuilderCommunicator = ({ isInitialLoadFromSwarm, publicationId }: Props): ReturnProps => {
  // On the first fetch from the server we check the initial referrer and save in state for the session.
  // This way we know not to redirect away from the preview a user is seeing in the Design Lab in swarm.
  // If a user tries to view the draft version from the real site and not in an iframe thats a child of swarm, the useEffect below will redirect them to the live version.
  const [initialLoadFromSwarm] = useState(isInitialLoadFromSwarm);
  const [sectionId, setSectionId] = useState<string | null>(null);
  const [nodeId, setNodeId] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname + location.search;
  const isDraft = location.search.includes('version=draft');

  useEffect(() => {
    if (!initialLoadFromSwarm && isDraft) {
      navigate('.', { replace: true });
    }
  }, [navigate, initialLoadFromSwarm, isDraft]);

  const revalidate = useCallback(
    function revalidate() {
      navigate(path, { replace: true });
    },
    [navigate, path],
  );

  useEffect(() => {
    const handleMessage = (event: any) => {
      const { data } = event;
      const isPublicationId = data?.publicationId === publicationId;

      if (isPublicationId && initialLoadFromSwarm) {
        // Check for revalidation from Swarm client
        if (data?.status === 'refresh') {
          revalidate();
        }

        // Check for highlighted sections / nodes from Swarm client
        setSectionId(data?.sectionId);
        setNodeId(data?.nodeId);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [revalidate, initialLoadFromSwarm]);

  return { highlightedSectionId: sectionId, highlightedNodeId: nodeId };
};

export default useWebBuilderCommunicator;
